// @flow

/**
 * Module dependencies.
 */

import { chain, findKey, get, includes, isEmpty, map } from 'lodash';
import { metatags, pages } from 'core/entities';
import config from 'config';

/**
 * Get pathname.
 */

export function getPathname(path: string, language: string) {
  return isEmpty(path) ? `/${language}` : `/${language}/${path}`;
}

/**
 * Get urls by key.
 */

export function getUrlsByKey(urlKey: string) {
  const paths = get(pages, `${urlKey}`);

  return map(paths, (path: string, language: string) => `/${language}/${path}`);
}

/**
 * Get url base on language.
 */

export function getUrlBasedOnLanguage(urlKey: string, language: string) {
  const path = get(pages, `${urlKey}.${language}`);

  return getPathname(path, language);
}

/**
 * Get language from url location.
 */

export function getLanguageFromUrl(url: string) {
  const fallbackLng = get(config, 'i18n.fallbackLng', {});
  const languages = get(config, 'i18n.whitelist', {});
  const urlLng = chain(url)
    .split('/')
    .tail()
    .head()
    .value();

  return includes(languages, urlLng) ? urlLng : fallbackLng;
}

/**
 * Get page url without language.
 */

export function getPageUrlWithouLanguage(urlKey: string, language: string) {
  const previousLanguage = getLanguageFromUrl(urlKey);
  const previousPathname = urlKey.replace(`/${previousLanguage}/`, '');

  return chain(pages)
    .find([previousLanguage, previousPathname])
    .get(language)
    .value();
}

/**
 * Get page url key from entities.
 */

export function getPageUrlKeyFromEntities(url: string, language: string) {
  const pageUrl = getPageUrlWithouLanguage(url, language);
  const urlKey = findKey(pages, [language, pageUrl]);

  return urlKey ? urlKey : 'home';
}

/**
 * Get translated metatags.
 */

export function getTranslatedMetatags(urlKey: string, language: string) {
  const fallbackMeta = get(metatags, `home.${language}`, {});
  const meta = get(metatags, `${urlKey}.${language}`, {});

  return isEmpty(meta) ? fallbackMeta : meta;
}

/**
 * Get metatags.
 */

export function getMetatags(url: string, language: string) {
  const urlKey = getPageUrlKeyFromEntities(url, language);

  return getTranslatedMetatags(urlKey, language);
}



// WEBPACK FOOTER //
// ../src/core/utils/routes.js